import React from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Sparkles, Calendar, Coffee, Cake, Copy, CheckCircle2 } from 'lucide-react';

const CustomerPortalRewards = ({
    rewards,
    customer,
    primaryColor,
    handleRedeem,
    redeeming,
    activeRewardCodes = []
}) => {
    const { t } = useTranslation();

    return (
        <div className="fade-in">
            {/* Active Rewards Section */}
            {activeRewardCodes.length > 0 && (
                <div style={{
                    marginBottom: '1.5rem',
                    background: 'var(--bg-card)',
                    borderRadius: '20px',
                    padding: '1.25rem',
                    border: `2px dashed ${primaryColor}`,
                    boxShadow: `0 10px 25px ${primaryColor}15`
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                        <div style={{
                            background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
                            color: 'white',
                            padding: '8px',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Sparkles size={18} />
                        </div>
                        <div>
                            <h3 style={{ fontSize: '0.95rem', fontWeight: 900, margin: 0 }}>
                                Aktif Ödülleriniz
                            </h3>
                            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: 0, fontWeight: 600 }}>
                                Sepetten kullanabilirsiniz
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {activeRewardCodes.filter(it => it.status === 'active').map(it => (
                            <div key={it.id} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: 'var(--bg-main)',
                                padding: '1rem 1.25rem',
                                borderRadius: '16px',
                                border: '1px solid var(--border)',
                                transition: 'all 0.2s'
                            }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {it.content?.includes(': ') ? it.content.split(': ')[1].split(' (')[0] : it.content}
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    padding: '6px 14px',
                                    borderRadius: '10px',
                                    marginLeft: '0.75rem',
                                    flexShrink: 0,
                                    background: '#22c55e15',
                                    color: '#16a34a',
                                }}>
                                    Sepetten Kullan
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Available Rewards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {rewards.length === 0 ? (
                    <div className="card" style={{ gridColumn: '1 / -1', padding: '3rem', textAlign: 'center', opacity: 0.5 }}>
                        <Sparkles size={48} style={{ margin: '0 auto 1rem' }} />
                        <p style={{ margin: 0 }}>{t('discounts.noRewards')}</p>
                    </div>
                ) : (
                    rewards.map(reward => {
                        const currentPoints = customer.points || 0;
                        const requiredPoints = reward.requiredPoints || 100;
                        const progress = Math.min((currentPoints / requiredPoints) * 100, 100);
                        const isUpcoming = reward.startDate && new Date(reward.startDate) > new Date();
                        const hasActiveInstance = (customer.activeRewards || []).some(
                            r => r.discountId === reward.id && r.status === 'active'
                        );
                        const canClaim = currentPoints >= requiredPoints && !isUpcoming && !hasActiveInstance;

                        // Circular Progress Math
                        const radius = 35;
                        const circumference = 2 * Math.PI * radius;
                        const offset = circumference - (progress / 100) * circumference;

                        return (
                            <div key={reward.id} className="card-premium" style={{ textAlign: 'center', padding: '1.5rem 1rem', opacity: isUpcoming ? 0.7 : 1 }}>
                                <div style={{ position: 'relative', width: '90px', height: '90px', margin: '0 auto 1rem' }}>
                                    <svg style={{ transform: 'rotate(-90deg)', width: '90px', height: '90px' }}>
                                        <circle cx="45" cy="45" r={radius} fill="transparent" stroke="var(--bg-main)" strokeWidth="8" />
                                        <circle cx="45" cy="45" r={radius} fill="transparent" stroke={isUpcoming ? '#94a3b8' : primaryColor} strokeWidth="8" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }} />
                                    </svg>
                                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: progress === 100 && !isUpcoming ? primaryColor : 'var(--text-muted)' }}>
                                        {reward.rewardType === 'coffee' ? <Coffee size={32} /> : reward.rewardType === 'cake' ? <Cake size={32} /> : <Gift size={32} />}
                                    </div>
                                </div>
                                <h4 style={{ fontSize: '0.875rem', fontWeight: 800, margin: '0 0 0.25rem 0' }}>{reward.title}</h4>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                                    {isUpcoming ? (
                                        <span style={{ color: '#f59e0b', fontWeight: 700 }}>
                                            <Calendar size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                                            {t('discounts.comingSoon')} ({new Date(reward.startDate).toLocaleDateString()})
                                        </span>
                                    ) : canClaim ? (
                                        <span style={{ color: '#22c55e', fontWeight: 700 }}>{t('common.active')}</span>
                                    ) : (
                                        t('discounts.pointsNeeded', { points: requiredPoints - currentPoints })
                                    )}
                                </div>
                                <button
                                    onClick={() => handleRedeem(reward)}
                                    disabled={!canClaim || redeeming === reward.id}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                        background: canClaim ? primaryColor : '#f1f5f9',
                                        color: canClaim ? 'var(--text-on-primary)' : '#94a3b8',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 800,
                                        cursor: canClaim ? 'pointer' : 'not-allowed'
                                    }}
                                >
                                    {redeeming === reward.id ? t('common.loading') : isUpcoming ? t('discounts.comingSoon') : hasActiveInstance ? 'Zaten Alındı' : t('discounts.claimReward')}
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default CustomerPortalRewards;
